<template>
  <transition
    :duration="1000"
    v-on:before-leave="beforeLeave"
    v-on:after-enter="afterEnter">
    <div>
      <div
        class="globe"
        v-if="!webGLProblems">
        <header
          class="globe__welcome-container"
          v-bind:class="{ active: showIntro && !introPlayed }">
          <div
            class="globe__welcome">
            <h1>SURREY, BRITISH COLUMBIA, CANADA</h1>
            <h2>North America’s Next Metropolitan Centre</h2>
          </div>
        </header>
        <div
          class="globe__3d-container">
          <canvas
            v-bind:class="{'globe__canvas--loading': loading, 'globe__canvas': true}"
            >
          </canvas>
        </div>
        <globe-details
          v-if="item"
          v-bind:item="item">
        </globe-details>
      </div>
      <div
        class="webgl-error"
        v-if="webGLProblems">
        <p>Oops! You’ve come here because your web browser doesn’t support WebGL. You can still access part of the site and explore the local amenities the City of Surrey has to offer by clicking <a href="/#/local">here</a>.
        <p>To have the complete experience please download Google Chrome <a href="https://www.google.com/chrome/" target="_blank">here</a> or if your browser does support WebGL, please ensure it is enabled. To learn more about enabling WebGL on your browser click <a href="https://get.webgl.org/" target="_blank">here</a>.</p>
      </div>
      <global-nav v-if="!loading"
        v-bind:config="navItems.list()"
        v-bind:opened="navOpened"
        v-bind:toggleActive="toggleActive"
        v-bind:description="navDescription"></global-nav>
    </div>
  </transition>
</template>
<script>
  import * as basicLightbox from 'basiclightbox';
  import { isUndefined } from 'lodash';
  import config from '@/services/Config';
  import FlyGlobe from '@/services/FlyGlobe';
  import GlobalNav from '@/components/GlobalNav';
  import GlobeDetails from './GlobeDetails';

  export default {
    name: 'Globe',
    data() {
      return {
        flyGlobe: new FlyGlobe({
          SELECTOR: '.globe__3d-container',
        }),
        item: null,
        loading: true,
        navItems: null,
        navOpened: false,
        toggleActive: false,
        showIntro: isUndefined(this.$route.params.id),
        introPlayed: false,
        transitionedIn: false,
        navDescription: null,
        metaData: null,
        webGLProblems: null,
      };
    },
    components: { GlobeDetails, GlobalNav },
    mounted() {
      this.introPlayed = window.sessionStorage.getItem('sdIntroPlayed');
      this.detectWebGL()
        .then(() => this.fetchData())
        .catch((problems) => {
          this.webGLProblems = problems;
        });
      this.fetchData();
    },
    methods: {
      detectWebGL() {
        return new Promise((resolve, reject) => {
          if (!window.WebGLRenderingContext) {
            reject({ webGLSupported: false, webGLEnabled: false });
          } else {
            const canvas = document.createElement('canvas');
            if (!canvas.getContext('webgl') && !canvas.getContext('experimental-webgl')) {
              reject({ webGLSupported: true, webGLEnabled: false });
            } else {
              resolve();
            }
            canvas.remove();
          }
        });
      },
      openDestination(link) {
        this.item = link;
        return this.flyGlobe.setDestination(link);
      },
      introAnimation() {
        this.showIntro = false;
        this.flyGlobe.introAnimation()
          .then(() => {
            if (!window.matchMedia('(max-width: 600px)').matches) {
              this.navOpened = true;
            }
            this.toggleActive = true;
            if (!this.introPlayed) {
              basicLightbox.create(`
                <div class="modal modal--globe">
                  <h1>${this.metaData.globalWelcomeTitle}</h1>
                  <h2>${this.metaData.globalWelcomeSubTitle}</h2>
                  <p>${this.metaData.globalWelcomeBody1}</p>
                  <p>${this.metaData.globalWelcomeBody2}</p>
                </div>
              `).show();
            }
            this.introPlayed = true;
            window.sessionStorage.setItem('sdIntroPlayed', true);
          });
      },
      transitionOut() {
        this.navOpened = false;
        this.item = null;
        if (!this.webGLProblems) {
          this.flyGlobe.outroAnimation();
        }
      },
      fetchData() {
        config.get()
          .then((data) => {
            const loadStart = Date.now();
            this.navItems = data.global.nav;
            this.navDescription = data.metaData.globalDescription;
            this.metaData = data.metaData;
            this.flyGlobe.init(this.navItems.list())
              .then(() => {
                if (isUndefined(this.$route.params.id)) {
                  const delay = Math.max(2500 - (Date.now() - loadStart), 0);
                  setTimeout(() => {
                    this.loading = false;
                    this.introAnimation();
                  }, delay);
                } else {
                  this.loading = false;

                  const item = this.navItems.find(this.$route.params.id);
                  this.openDestination(item)
                    .then(() => {
                      if (!window.matchMedia('(max-width: 600px)').matches) {
                        this.navOpened = true;
                      }
                      this.toggleActive = true;
                    });
                }
              });
          });
      },
      beforeLeave() {
        this.transitionOut();
      },
      afterEnter() {
        this.transitionedIn = true;
      },
    },
    beforeRouteUpdate(to, from, next) {
      if (to.params.id !== from.params.id) {
        this.openDestination(this.navItems.find(to.params.id));
      }
      if (to.name === 'local.overview') {
        setTimeout(next, 10000);
      } else {
        if (window.matchMedia('(max-width: 600px)').matches) {
          this.navOpened = false;
        }
        next();
      }
    },
  };
</script>
<style lang="scss" scoped>
  @import '../main.scss';

  .globe__3d-container {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    overflow: hidden;
    .globe__canvas {
      display: block;
      position: fixed;
      width: 100%;
      height: 100%;
      z-index: 10;
      opacity: 1;
      &.globe__canvas--loading {
        opacity: 0;
      }
    }
  }

  .globe__welcome-container {
    width: 100%;
    height: 100%;
    position: absolute;

    left: 0;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 1s ease-in-out;
    &.active {
      opacity: 1;
      z-index: 15;
    }
  }

  .webgl-error {
    text-align: center;
    font-size: 2.2rem;
    width: 400px;
    margin: 100px auto;
    text-transform: none;
    a {
      color: $appPrimary;
    }
  }

  .globe__welcome {
    color: $appPrimary;
    text-align: center;

    h1, h2 {
      margin: 0;
      padding: 0;

    }

    h1 {
      font-size: 4.5rem;
      letter-spacing: 16px;
      line-height: 4.5rem;
    }

    h2 {
      letter-spacing: 13.4px;
      font-size: 3.2rem;
      font-weight: 700;
      line-height: 3.2rem;
    }
  }

  .v-leave-active {
    .globe-details {
      opacity: 0;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/Globe.vue